import { useFrame, useThree } from '@react-three/fiber';
import { useRef, useMemo, useEffect } from 'react';
import * as THREE from 'three';
import { useExperienceStore } from '../state/useExperienceStore';
import gsap from 'gsap';

const vertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position, 1.0);
  }
`;

const fragmentShader = `
  uniform float uTime;
  uniform float uProgress;
  uniform sampler2D uTexture;
  varying vec2 vUv;

  void main() {
    vec2 uv = vUv;
    
    // Subtle fluid warping
    float noise = sin(uv.x * 10.0 + uTime) * 0.05 + cos(uv.y * 10.0 + uTime) * 0.05;
    uv += noise * uProgress;
    
    // Fade color
    vec3 color = vec3(0.0); // Black wash
    float alpha = uProgress;
    
    gl_FragColor = vec4(color, alpha);
  }
`;

export const ExperienceTransitions = () => {
  const meshRef = useRef<THREE.Mesh>(null!);
  const mode = useExperienceStore((state) => state.mode);
  const prevMode = useRef(mode);
  
  const uniforms = useMemo(() => ({
    uTime: { value: 0 },
    uProgress: { value: 0 },
  }), []);

  useEffect(() => {
    if (mode !== prevMode.current) {
      // Trigger flash/wipe transition
      import('../lib/sound').then(({ sounds }) => sounds.play('transition', 0.3));

      gsap.fromTo(uniforms.uProgress, 
        { value: 0 }, 
        { 
          value: 1, 
          duration: 0.4, 
          ease: 'power4.inOut',
          yoyo: true,
          repeat: 1
        }
      );
      prevMode.current = mode;
    }
  }, [mode, uniforms.uProgress]);

  useFrame((state) => {
    uniforms.uTime.value = state.clock.getElapsedTime();
  });

  return (
    <mesh ref={meshRef} frustumCulled={false} renderOrder={999}>
      <planeGeometry args={[2, 2]} />
      <shaderMaterial
        transparent
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        uniforms={uniforms}
        depthTest={false}
        depthWrite={false}
      />
    </mesh>
  );
};
